/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import classNames from 'classnames';

import type { CommonProps } from '../../_common/types';

type MoreFilesProps = {
  /** The number of files to show */
  count: number;
  /** Callback function triggered when the more files button is clicked */
  onClick?: () => void;
  /** Whether the more files button is active. Should be true when it opens the menu of files. */
  isActive?: boolean;
};

/**
 * The component is a button that is meant to open a menu of files when clicked.
 * It should be used in a chat when you have more files attached to the message than should be shown at once.
 *
 * @alpha - Changes to this component may be breaking.
 */
const MoreFilesComponent = ({
  count,
  onClick,
  className,
  style,
  htmlAttributes,
  ref,
  isActive,
  ...restProps
}: CommonProps<'button', MoreFilesProps>) => {
  const classes = classNames('ndl-ai-more-files', className, {
    'ndl-active': isActive,
  });

  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      className={classes}
      style={style}
      aria-label={`${count} more files`}
      aria-expanded={isActive}
      {...restProps}
      {...htmlAttributes}
    >
      +{count}
    </button>
  );
};

export { MoreFilesComponent as MoreFiles };
